// src/lib/layouts/registry.tsx
import React from 'react';
import { ModelXFrame } from '@/components/layouts/renderers/ModelXFrame';
import { ChoqueiFrame } from '@/components/layouts/renderers/ChoqueiFrame';
import { BreakingNewsFrame } from '@/components/layouts/renderers/BreakingNewsFrame';
import { NewspaperFrame } from '@/components/layouts/renderers/NewspaperFrame';
import { InstagramNewsFrame } from '@/components/layouts/renderers/InstagramNewsFrame';
import { FactQuoteFrame } from '@/components/layouts/renderers/FactQuoteFrame';
import { SatisfatorioFrame } from '@/components/layouts/renderers/SatisfatorioFrame';
import { TripleSplitFrame } from '@/components/layouts/renderers/TripleSplitFrame';
import { NewsPostFrame } from '@/components/layouts/renderers/NewsPostFrame';
import { CortesFrame } from '@/components/layouts/renderers/CortesFrame';
import { FofocaFrame } from '@/components/layouts/renderers/FofocaFrame';
import { CirculoFrame } from '@/components/layouts/renderers/CirculoFrame';
import { buildDefaults } from './buildDefaults';
import { circuloConfigForPreset } from './resolveCirculoPreset';
import { resolveCirculoBackground } from './resolveCirculoBackground';
import { applyBrandKit } from './applyBrandKit';
import { CLIP_RATIO_MIN, CLIP_RATIO_MAX } from './clampClipRatio';
import { FILLER_SPEED_MIN, FILLER_SPEED_MAX } from './clampFillerSpeed';
import { resolveFofocaStoryMedia } from './resolveFofocaStoryMedia';
import { resolveCortesGeometry, resolveCortesPreset } from './resolveCortesPreset';
import type {
  BrandKitLayoutSource, BreakingNewsConfig, ChoqueiConfig, CortesConfig, FactQuoteConfig, FofocaConfig,
  CirculoConfig, FieldDescriptor, InstagramNewsConfig, LayoutDefaultsContext,
  LayoutRendererProps, ModelXConfig, NewsPostConfig, NewspaperConfig, PostLayoutConfig, SatisfatorioConfig, TripleSplitConfig,
} from './types';

export interface PostLayoutDef<C extends PostLayoutConfig> {
  kind: C['kind'];
  mode: 'video' | 'static_image' | 'auto';
  /** How the clip video fills the media region. Defaults to 'contain'. */
  mediaFit?: 'contain' | 'cover';
  /**
   * When true, the renderer adapts to any composition aspect ratio (it reads
   * useVideoConfig() and uses relative units), so PostFrameComposition renders
   * it directly at the real composition size instead of wrapping it in a fixed
   * 1080×1920 design canvas scaled non-uniformly to fill the frame. Leaving this
   * false keeps the scale wrapper for renderers that hardcode 1080×1920 coords.
   */
  aspectAware?: boolean;
  /** Composition aspect this layout requires, regardless of the source video. */
  requiredAspect?: 'landscape' | 'portrait';
  labelKey: string;
  fields: FieldDescriptor[] | ((config: PostLayoutConfig) => FieldDescriptor[]);
  build: (ctx: LayoutDefaultsContext, bk: BrandKitLayoutSource | null) => C;
  Renderer: React.FC<LayoutRendererProps<C>>;
}

const HEADLINE_STYLE_FIELDS: FieldDescriptor[] = [
  { type: 'color', key: 'headlineColor', labelKey: 'projectClips.postLayouts.common.headlineColor' },
  { type: 'font', key: 'headlineFontFamily', urlKey: 'headlineFontUrl', labelKey: 'projectClips.postLayouts.common.headlineFont' },
  { type: 'slider', key: 'headlineFontSize', labelKey: 'projectClips.postLayouts.common.headlineSize', min: 24, max: 160, step: 1, unit: 'px' },
];

const MODEL_X: PostLayoutDef<ModelXConfig> = {
  kind: 'model_x',
  mode: 'video',
  requiredAspect: 'portrait',
  labelKey: 'projectClips.speakerLayout.twitterLayout',
  fields: [
    { type: 'image', key: 'avatarUrl', labelKey: 'projectClips.speakerLayout.twitterAvatarUrl' },
    { type: 'text', key: 'displayName', labelKey: 'projectClips.speakerLayout.twitterDisplayName' },
    { type: 'text', key: 'handle', labelKey: 'projectClips.speakerLayout.twitterHandle' },
    { type: 'text', key: 'postText', labelKey: 'projectClips.speakerLayout.twitterTweetText', multiline: true },
    ...HEADLINE_STYLE_FIELDS,
  ],
  build: (ctx, bk) => applyBrandKit(buildDefaults('model_x', ctx), bk) as ModelXConfig,
  Renderer: ModelXFrame,
};

const CHOQUEI: PostLayoutDef<ChoqueiConfig> = {
  kind: 'choquei',
  mode: 'auto',
  requiredAspect: 'portrait',
  labelKey: 'projectClips.postLayouts.choquei.label',
  fields: [
    { type: 'image', key: 'imageUrl', labelKey: 'projectClips.postLayouts.choquei.image' },
    { type: 'image', key: 'logoUrl', labelKey: 'projectClips.postLayouts.choquei.logo' },
    { type: 'text', key: 'brandName', labelKey: 'projectClips.postLayouts.choquei.brandName' },
    { type: 'text', key: 'headline', labelKey: 'projectClips.postLayouts.choquei.headline', multiline: true },
    { type: 'color', key: 'accentColor', labelKey: 'projectClips.postLayouts.choquei.accent' },
  ],
  build: (ctx, bk) => applyBrandKit(buildDefaults('choquei', ctx), bk) as ChoqueiConfig,
  Renderer: ChoqueiFrame,
};

const BREAKING_NEWS: PostLayoutDef<BreakingNewsConfig> = {
  kind: 'breaking_news',
  mode: 'auto',
  requiredAspect: 'portrait',
  labelKey: 'projectClips.postLayouts.breakingNews.label',
  fields: [
    { type: 'image', key: 'imageUrl', labelKey: 'projectClips.postLayouts.common.image' },
    { type: 'image', key: 'logoUrl', labelKey: 'projectClips.postLayouts.breakingNews.logo' },
    { type: 'text', key: 'channelName', labelKey: 'projectClips.postLayouts.breakingNews.channel' },
    { type: 'text', key: 'tag', labelKey: 'projectClips.postLayouts.breakingNews.tag' },
    { type: 'text', key: 'headline', labelKey: 'projectClips.postLayouts.common.headline', multiline: true },
    { type: 'color', key: 'accentColor', labelKey: 'projectClips.postLayouts.common.accent' },
  ],
  build: (ctx, bk) => applyBrandKit(buildDefaults('breaking_news', ctx), bk) as BreakingNewsConfig,
  Renderer: BreakingNewsFrame,
};

const NEWSPAPER: PostLayoutDef<NewspaperConfig> = {
  kind: 'newspaper',
  mode: 'auto',
  requiredAspect: 'portrait',
  labelKey: 'projectClips.postLayouts.newspaper.label',
  fields: [
    { type: 'image', key: 'imageUrl', labelKey: 'projectClips.postLayouts.common.image' },
    { type: 'text', key: 'sourceName', labelKey: 'projectClips.postLayouts.newspaper.source' },
    { type: 'text', key: 'headline', labelKey: 'projectClips.postLayouts.common.headline', multiline: true },
    { type: 'text', key: 'kicker', labelKey: 'projectClips.postLayouts.newspaper.kicker' },
    { type: 'color', key: 'accentColor', labelKey: 'projectClips.postLayouts.common.accent' },
  ],
  build: (ctx, bk) => applyBrandKit(buildDefaults('newspaper', ctx), bk) as NewspaperConfig,
  Renderer: NewspaperFrame,
};

const INSTAGRAM: PostLayoutDef<InstagramNewsConfig> = {
  kind: 'instagram',
  mode: 'auto',
  requiredAspect: 'portrait',
  labelKey: 'projectClips.postLayouts.instagram.label',
  fields: [
    { type: 'image', key: 'imageUrl', labelKey: 'projectClips.postLayouts.common.image' },
    { type: 'image', key: 'avatarUrl', labelKey: 'projectClips.postLayouts.instagram.avatar' },
    { type: 'text', key: 'handle', labelKey: 'projectClips.postLayouts.instagram.handle' },
    { type: 'text', key: 'caption', labelKey: 'projectClips.postLayouts.instagram.caption', multiline: true },
    { type: 'text', key: 'likes', labelKey: 'projectClips.postLayouts.instagram.likes' },
  ],
  build: (ctx, bk) => applyBrandKit(buildDefaults('instagram', ctx), bk) as InstagramNewsConfig,
  Renderer: InstagramNewsFrame,
};

const FACT_QUOTE: PostLayoutDef<FactQuoteConfig> = {
  kind: 'fact_quote',
  mode: 'video',
  requiredAspect: 'portrait',
  labelKey: 'projectClips.postLayouts.factQuote.label',
  fields: [
    { type: 'text', key: 'headline', labelKey: 'projectClips.postLayouts.common.headline', multiline: true },
    { type: 'color', key: 'textColor', labelKey: 'projectClips.postLayouts.factQuote.textColor' },
    { type: 'font', key: 'headlineFontFamily', urlKey: 'headlineFontUrl', labelKey: 'projectClips.postLayouts.common.headlineFont' },
    { type: 'slider', key: 'headlineFontSize', labelKey: 'projectClips.postLayouts.common.headlineSize', min: 24, max: 160, step: 1, unit: 'px' },
    { type: 'color', key: 'scrimColor', labelKey: 'projectClips.postLayouts.factQuote.scrim' },
  ],
  build: (ctx, bk) => applyBrandKit(buildDefaults('fact_quote', ctx), bk) as FactQuoteConfig,
  Renderer: FactQuoteFrame,
};

const SATISFATORIO: PostLayoutDef<SatisfatorioConfig> = {
  kind: 'satisfatorio',
  mode: 'video',
  mediaFit: 'cover',
  aspectAware: true,
  requiredAspect: 'portrait',
  labelKey: 'projectClips.postLayouts.satisfatorio.label',
  fields: (config) => {
    const c = config as SatisfatorioConfig;
    const video = 'projectClips.postLayouts.satisfatorio.sectionVideo';
    const titleSection = 'projectClips.postLayouts.satisfatorio.sectionTitle';
    const layout = 'projectClips.postLayouts.satisfatorio.sectionLayout';
    const fields: FieldDescriptor[] = [
      { type: 'video', key: 'fillerUrl', labelKey: 'projectClips.postLayouts.satisfatorio.filler', sectionKey: video },
      { type: 'slider', key: 'fillerSpeed', labelKey: 'projectClips.postLayouts.satisfatorio.speed', min: FILLER_SPEED_MIN, max: FILLER_SPEED_MAX, step: 0.1, unit: 'x', sectionKey: video },
      { type: 'text', key: 'title', labelKey: 'projectClips.postLayouts.satisfatorio.title', sectionKey: titleSection },
      {
        type: 'toggle',
        key: 'titleFrame',
        labelKey: 'projectClips.postLayouts.satisfatorio.titleFrame',
        sectionKey: titleSection,
        options: [
          { value: 'none', labelKey: 'projectClips.postLayouts.satisfatorio.frameNone' },
          { value: 'pill', labelKey: 'projectClips.postLayouts.satisfatorio.framePill' },
          { value: 'dark', labelKey: 'projectClips.postLayouts.satisfatorio.frameDark' },
          { value: 'accent', labelKey: 'projectClips.postLayouts.satisfatorio.frameAccent' },
          { value: 'outline', labelKey: 'projectClips.postLayouts.satisfatorio.frameOutline' },
          { value: 'bar', labelKey: 'projectClips.postLayouts.satisfatorio.frameBar' },
        ],
      },
    ];
    if (c.titleFrame === 'accent' || c.titleFrame === 'bar') {
      fields.push({ type: 'color', key: 'titleAccentColor', labelKey: 'projectClips.postLayouts.satisfatorio.titleColor', sectionKey: titleSection });
    }
    fields.push(
      { type: 'font', key: 'titleFontFamily', urlKey: 'titleFontUrl', labelKey: 'projectClips.postLayouts.common.headlineFont', sectionKey: titleSection },
      { type: 'slider', key: 'titleFontSize', labelKey: 'projectClips.postLayouts.common.headlineSize', min: 24, max: 160, step: 1, unit: 'px', sectionKey: titleSection },
    );
    fields.push(
      {
        type: 'toggle',
        key: 'clipPosition',
        labelKey: 'projectClips.postLayouts.satisfatorio.clipPosition',
        sectionKey: layout,
        options: [
          { value: 'top', labelKey: 'projectClips.postLayouts.satisfatorio.clipOnTop' },
          { value: 'bottom', labelKey: 'projectClips.postLayouts.satisfatorio.clipOnBottom' },
        ],
      },
      { type: 'slider', key: 'clipRatio', labelKey: 'projectClips.postLayouts.satisfatorio.ratio', min: CLIP_RATIO_MIN, max: CLIP_RATIO_MAX, step: 1, sectionKey: layout },
    );
    return fields;
  },
  build: (ctx) => buildDefaults('satisfatorio', ctx) as SatisfatorioConfig,
  Renderer: SatisfatorioFrame,
};

const TRIPLE_SPLIT: PostLayoutDef<TripleSplitConfig> = {
  kind: 'triple_split',
  mode: 'video',
  mediaFit: 'cover',
  aspectAware: true,
  requiredAspect: 'portrait',
  labelKey: 'projectClips.postLayouts.tripleSplit.label',
  fields: (config) => {
    const c = config as TripleSplitConfig;
    const video = 'projectClips.postLayouts.satisfatorio.sectionVideo';
    const titleSection = 'projectClips.postLayouts.satisfatorio.sectionTitle';
    const fields: FieldDescriptor[] = [
      {
        type: 'video',
        key: 'fillerUrl',
        labelKey: 'projectClips.postLayouts.tripleSplit.fillerTop',
        sectionKey: video,
      },
      {
        type: 'video',
        key: 'bottomFillerUrl',
        labelKey: 'projectClips.postLayouts.tripleSplit.fillerBottom',
        urlsKey: 'bottomFillerUrls',
        fitsKey: 'bottomFillerFits',
        positionsKey: 'bottomFillerPositions',
        randomizeKey: 'bottomFillerRandomize',
        sectionKey: video,
      },
      { type: 'slider', key: 'fillerSpeed', labelKey: 'projectClips.postLayouts.satisfatorio.speed', min: FILLER_SPEED_MIN, max: FILLER_SPEED_MAX, step: 0.1, unit: 'x', sectionKey: video },
      { type: 'text', key: 'title', labelKey: 'projectClips.postLayouts.tripleSplit.title', sectionKey: titleSection },
      { type: 'text', key: 'title2', labelKey: 'projectClips.postLayouts.tripleSplit.title2', sectionKey: titleSection },
      {
        type: 'toggle',
        key: 'titleFrame',
        labelKey: 'projectClips.postLayouts.satisfatorio.titleFrame',
        sectionKey: titleSection,
        options: [
          { value: 'none', labelKey: 'projectClips.postLayouts.satisfatorio.frameNone' },
          { value: 'pill', labelKey: 'projectClips.postLayouts.satisfatorio.framePill' },
          { value: 'dark', labelKey: 'projectClips.postLayouts.satisfatorio.frameDark' },
          { value: 'accent', labelKey: 'projectClips.postLayouts.satisfatorio.frameAccent' },
          { value: 'outline', labelKey: 'projectClips.postLayouts.satisfatorio.frameOutline' },
          { value: 'bar', labelKey: 'projectClips.postLayouts.satisfatorio.frameBar' },
        ],
      },
    ];
    if (c.titleFrame === 'accent' || c.titleFrame === 'bar') {
      fields.push({ type: 'color', key: 'titleAccentColor', labelKey: 'projectClips.postLayouts.satisfatorio.titleColor', sectionKey: titleSection });
    }
    fields.push(
      { type: 'font', key: 'titleFontFamily', urlKey: 'titleFontUrl', labelKey: 'projectClips.postLayouts.common.headlineFont', sectionKey: titleSection },
      { type: 'slider', key: 'titleFontSize', labelKey: 'projectClips.postLayouts.common.headlineSize', min: 24, max: 160, step: 1, unit: 'px', sectionKey: titleSection },
    );
    return fields;
  },
  build: (ctx) => buildDefaults('triple_split', ctx) as TripleSplitConfig,
  Renderer: TripleSplitFrame,
};

const NEWS_POST: PostLayoutDef<NewsPostConfig> = {
  kind: 'news_post',
  mode: 'auto',
  requiredAspect: 'portrait',
  labelKey: 'projectClips.postLayouts.newsPost.label',
  fields: (config) => {
    const c = config as NewsPostConfig;
    const styleField: FieldDescriptor = {
      type: 'toggle',
      key: 'style',
      labelKey: 'projectClips.postLayouts.newsPost.style',
      options: [
        { value: 'choquei', labelKey: 'projectClips.postLayouts.newsPost.styleChoquei' },
        { value: 'breaking_news', labelKey: 'projectClips.postLayouts.newsPost.styleBreaking' },
        { value: 'newspaper', labelKey: 'projectClips.postLayouts.newsPost.styleNewspaper' },
        { value: 'instagram', labelKey: 'projectClips.postLayouts.newsPost.styleInstagram' },
      ],
    };
    const common: FieldDescriptor[] = [
      { type: 'image', key: 'imageUrl', labelKey: 'projectClips.postLayouts.common.image' },
      { type: 'text', key: 'brandName', labelKey: 'projectClips.postLayouts.newsPost.brandName' },
      { type: 'text', key: 'headline', labelKey: 'projectClips.postLayouts.common.headline', multiline: true },
    ];
    switch (c.style) {
      case 'breaking_news':
        return [styleField, ...common,
          { type: 'text', key: 'tag', labelKey: 'projectClips.postLayouts.newsPost.tag' },
          { type: 'image', key: 'logoUrl', labelKey: 'projectClips.postLayouts.breakingNews.logo' },
          { type: 'color', key: 'accentColor', labelKey: 'projectClips.postLayouts.common.accent' },
          ...HEADLINE_STYLE_FIELDS,
        ];
      case 'newspaper':
        return [styleField, ...common,
          { type: 'text', key: 'kicker', labelKey: 'projectClips.postLayouts.newsPost.kicker' },
          { type: 'color', key: 'accentColor', labelKey: 'projectClips.postLayouts.common.accent' },
          ...HEADLINE_STYLE_FIELDS,
        ];
      case 'instagram':
        return [styleField, ...common,
          { type: 'text', key: 'likes', labelKey: 'projectClips.postLayouts.newsPost.likes' },
          { type: 'image', key: 'avatarUrl', labelKey: 'projectClips.postLayouts.instagram.avatar' },
          ...HEADLINE_STYLE_FIELDS,
        ];
      case 'choquei':
      default:
        return [styleField, ...common,
          { type: 'image', key: 'logoUrl', labelKey: 'projectClips.postLayouts.choquei.logo' },
          { type: 'color', key: 'accentColor', labelKey: 'projectClips.postLayouts.common.accent' },
          ...HEADLINE_STYLE_FIELDS,
        ];
    }
  },
  build: (ctx, bk) => applyBrandKit(buildDefaults('news_post', ctx), bk) as NewsPostConfig,
  Renderer: NewsPostFrame,
};

const cortesPhotoVideoField = (slot: 1 | 2 | 3 | 4): FieldDescriptor => ({
  type: 'video',
  key: `photo${slot}Url`,
  urlsKey: `photo${slot}FillerUrls`,
  fitsKey: `photo${slot}FillerFits`,
  positionsKey: `photo${slot}FillerPositions`,
  randomizeKey: `photo${slot}FillerRandomize`,
  labelKey: `projectClips.postLayouts.cortes.photo${slot}`,
});

const CORTES: PostLayoutDef<CortesConfig> = {
  kind: 'cortes',
  mode: 'video',
  mediaFit: 'cover',
  requiredAspect: 'portrait',
  labelKey: 'projectClips.postLayouts.cortes.label',
  fields: (config) => {
    const cfg = config as CortesConfig;
    const geometry = resolveCortesGeometry(cfg);
    const hug = (cfg.headlineStyle?.boxMode ?? 'hug') === 'hug';
    const textBox = resolveCortesPreset(cfg) === 'caixa-vermelha';
    const textBar = resolveCortesPreset(cfg) === 'barra-branca';
    const fields: FieldDescriptor[] = [];
    if (!textBar) {
      fields.push({ type: 'image', key: 'backgroundUrl', labelKey: 'projectClips.postLayouts.cortes.background' });
    }
    const photoSlots: Array<{ slot: 1 | 2 | 3 | 4; rect: typeof geometry.photo1 }> = [
      { slot: 1, rect: geometry.photo1 },
      { slot: 2, rect: geometry.photo2 },
      { slot: 3, rect: geometry.photo3 },
      { slot: 4, rect: geometry.photo4 },
    ];
    photoSlots.forEach(({ slot, rect }) => {
      if (rect) fields.push(cortesPhotoVideoField(slot));
    });
    fields.push(
      { type: 'text', key: 'headline', labelKey: 'projectClips.postLayouts.common.headline', multiline: true },
      { type: 'color', key: 'headlineColor', labelKey: 'projectClips.postLayouts.cortes.headlineColor' },
      { type: 'font', key: 'headlineFontFamily', urlKey: 'headlineFontUrl', labelKey: 'projectClips.postLayouts.cortes.headlineFont' },
      {
        type: 'slider',
        key: 'headlineFontSize',
        labelKey: 'projectClips.postLayouts.cortes.headlineSize',
        min: 24,
        max: 160,
        step: 1,
        unit: 'px',
      },
    );
    if (!textBox) {
      fields.push({ type: 'text', key: 'handle', labelKey: 'projectClips.postLayouts.cortes.handle' });
    }
    fields.push(
      { type: 'color', key: 'headlineBoxColor', labelKey: 'projectClips.postLayouts.cortes.boxColor' },
    );
    if (hug && (geometry?.photo1 || geometry?.photo2 || geometry?.photo3 || geometry?.photo4 || cfg.headlineBoxWidth != null)) {
      fields.push({
        type: 'slider',
        key: 'headlineBoxWidth',
        labelKey: 'projectClips.postLayouts.cortes.boxWidth',
        min: 50,
        max: 100,
        step: 1,
        unit: '%',
      });
    }
    return fields;
  },
  build: (ctx, bk) => applyBrandKit(buildDefaults('cortes', ctx), bk) as CortesConfig,
  Renderer: CortesFrame,
};

const FOFOCA: PostLayoutDef<FofocaConfig> = {
  kind: 'fofoca',
  mode: 'video',
  mediaFit: 'cover',
  requiredAspect: 'portrait',
  labelKey: 'projectClips.postLayouts.fofoca.label',
  fields: (config) => {
    const media = resolveFofocaStoryMedia(config);
    const sectionMedia = 'projectClips.postLayouts.fofoca.sectionMedia';
    const mediaFields: FieldDescriptor[] = media === 'video'
      ? [
          { type: 'video', key: 'fillerUrl', labelKey: 'projectClips.postLayouts.fofoca.filler', sectionKey: sectionMedia },
          { type: 'slider', key: 'fillerSpeed', labelKey: 'projectClips.postLayouts.satisfatorio.speed', min: FILLER_SPEED_MIN, max: FILLER_SPEED_MAX, step: 0.1, unit: 'x', sectionKey: sectionMedia },
        ]
      : [
          { type: 'image', key: 'imageUrl', labelKey: 'projectClips.postLayouts.fofoca.image', allowVideoFrame: true, sectionKey: sectionMedia },
        ];
    return [
      { type: 'image', key: 'avatarUrl', labelKey: 'projectClips.postLayouts.fofoca.avatar', sectionKey: 'projectClips.postLayouts.fofoca.sectionProfile' },
      { type: 'text', key: 'profileName', labelKey: 'projectClips.postLayouts.fofoca.profileName', sectionKey: 'projectClips.postLayouts.fofoca.sectionProfile' },
      { type: 'text', key: 'caption', labelKey: 'projectClips.postLayouts.fofoca.caption', multiline: true, sectionKey: 'projectClips.postLayouts.fofoca.sectionProfile' },
      {
        type: 'toggle',
        key: 'storyMedia',
        labelKey: 'projectClips.postLayouts.fofoca.storyMedia',
        sectionKey: sectionMedia,
        hideLabel: true,
        options: [
          { value: 'image', labelKey: 'projectClips.postLayouts.fofoca.mediaImage' },
          { value: 'video', labelKey: 'projectClips.postLayouts.fofoca.mediaVideo' },
        ],
      },
      ...mediaFields,
      {
        type: 'toggle',
        key: 'clipPosition',
        labelKey: 'projectClips.postLayouts.fofoca.clipPosition',
        sectionKey: sectionMedia,
        options: [
          { value: 'right', labelKey: 'projectClips.postLayouts.fofoca.clipOnRight' },
          { value: 'left', labelKey: 'projectClips.postLayouts.fofoca.clipOnLeft' },
        ],
      },
      { type: 'image', key: 'badgeUrl', labelKey: 'projectClips.postLayouts.fofoca.badgeImage', sectionKey: 'projectClips.postLayouts.fofoca.sectionBadge' },
      { type: 'slider', key: 'badgeBorderRadius', labelKey: 'projectClips.postLayouts.fofoca.badgeBorderRadius', min: 0, max: 50, step: 1, unit: '%', sectionKey: 'projectClips.postLayouts.fofoca.sectionBadge' },
      { type: 'text', key: 'videoTitle', labelKey: 'projectClips.postLayouts.fofoca.videoTitle', sectionKey: 'projectClips.postLayouts.fofoca.sectionTitle' },
      { type: 'font', key: 'videoTitleFontFamily', urlKey: 'videoTitleFontUrl', labelKey: 'projectClips.postLayouts.fofoca.videoTitleFont', sectionKey: 'projectClips.postLayouts.fofoca.sectionTitle' },
      { type: 'slider', key: 'videoTitleFontSize', labelKey: 'projectClips.postLayouts.fofoca.videoTitleFontSize', min: 20, max: 96, step: 1, unit: 'px', sectionKey: 'projectClips.postLayouts.fofoca.sectionTitle' },
      { type: 'text', key: 'handle', labelKey: 'projectClips.postLayouts.fofoca.handle', sectionKey: 'projectClips.postLayouts.fofoca.sectionTitle' },
      { type: 'color', key: 'accentColor', labelKey: 'projectClips.postLayouts.fofoca.accent', sectionKey: 'projectClips.postLayouts.fofoca.sectionTitle' },
    ];
  },
  build: (ctx, bk) => applyBrandKit(buildDefaults('fofoca', ctx), bk) as FofocaConfig,
  Renderer: FofocaFrame,
};

const CIRCULO: PostLayoutDef<CirculoConfig> = {
  kind: 'circulo',
  mode: 'video',
  mediaFit: 'cover',
  aspectAware: true,
  requiredAspect: 'portrait',
  labelKey: 'projectClips.postLayouts.circulo.label',
  fields: (config) => {
    const c = config as CirculoConfig;
    const media = c.backgroundMedia
      ?? (resolveCirculoBackground(c) === 'image' ? 'image' : 'video');
    const sectionBg = 'projectClips.postLayouts.circulo.sectionBackground';
    const fields: FieldDescriptor[] = [
      {
        type: 'toggle',
        key: 'preset',
        labelKey: 'projectClips.postLayouts.circulo.arc',
        options: [
          { value: 'cima', labelKey: 'projectClips.postLayouts.circulo.arcUpper' },
          { value: 'baixo', labelKey: 'projectClips.postLayouts.circulo.arcLower' },
        ],
      },
      { type: 'color', key: 'backgroundColor', labelKey: 'projectClips.postLayouts.circulo.backgroundColor', sectionKey: sectionBg },
      {
        type: 'toggle',
        key: 'backgroundMedia',
        labelKey: 'projectClips.postLayouts.circulo.backgroundMedia',
        sectionKey: sectionBg,
        hideLabel: true,
        options: [
          { value: 'image', labelKey: 'projectClips.postLayouts.circulo.mediaImage' },
          { value: 'video', labelKey: 'projectClips.postLayouts.circulo.mediaVideo' },
        ],
      },
    ];
    if (media === 'image') {
      fields.push({ type: 'image', key: 'backgroundUrl', labelKey: 'projectClips.postLayouts.circulo.still', allowVideoFrame: true, sectionKey: sectionBg });
    } else {
      fields.push({
        type: 'video',
        key: 'fillerUrl',
        urlsKey: 'fillerUrls',
        fitsKey: 'fillerFits',
        positionsKey: 'fillerPositions',
        randomizeKey: 'fillerRandomize',
        labelKey: 'projectClips.postLayouts.circulo.filler',
        sectionKey: sectionBg,
      });
      fields.push({
        type: 'slider',
        key: 'fillerSpeed',
        labelKey: 'projectClips.postLayouts.satisfatorio.speed',
        min: FILLER_SPEED_MIN,
        max: FILLER_SPEED_MAX,
        step: 0.1,
        unit: 'x',
        sectionKey: sectionBg,
      });
    }
    return fields;
  },
  build: () => circuloConfigForPreset('baixo'),
  Renderer: CirculoFrame,
};

type Kind = 'satisfatorio' | 'triple_split' | 'fofoca' | 'model_x' | 'news_post' | 'choquei' | 'breaking_news' | 'newspaper' | 'instagram' | 'fact_quote' | 'cortes' | 'circulo';

// `any` here is intentional: each renderer is an FC over a different config type
// and can't share a single non-any PostLayoutDef element type without forcing every
// call site to narrow. Call sites index by a known kind.
export const POST_LAYOUTS: Record<Kind, PostLayoutDef<any>> = {
  satisfatorio: SATISFATORIO,
  triple_split: TRIPLE_SPLIT,
  model_x: MODEL_X,
  news_post: NEWS_POST,
  choquei: CHOQUEI,
  breaking_news: BREAKING_NEWS,
  newspaper: NEWSPAPER,
  instagram: INSTAGRAM,
  fact_quote: FACT_QUOTE,
  cortes: CORTES,
  fofoca: FOFOCA,
  circulo: CIRCULO,
};

/** Layouts exposed in the picker, in display order.
 *  Cortes is offered as preset cards, not as a generic kind button.
 *  Legacy news kinds (news_post, choquei, breaking_news, newspaper, instagram)
 *  stay registered for restore of old clips but are not picker entries. */
export const POST_LAYOUT_ORDER: Kind[] = [
  'satisfatorio',
  'triple_split',
  'fofoca',
  'model_x',
  'fact_quote',
];

export { CORTES_PRESET_IDS as CORTES_PICKER_PRESETS } from './resolveCortesPreset';

/** Full launchable catalog, used by the admin enforced-layout picker. */
export const POST_LAYOUT_ADMIN_ORDER: Kind[] = [
  'satisfatorio',
  'triple_split',
  'model_x',
  'fact_quote',
  'fofoca',
  'cortes',
  'circulo',
];
